import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Code,
  Smartphone,
  Database,
  Cpu,
  Layers,
  Globe,
  GitBranch,
} from "lucide-react";

const profiles = [
  {
    id: "react-native",
    title: "React Native Developer",
    desc: "Covers JavaScript, React, and React Native interview prep including coding and theory questions.",
    icon: <Smartphone size={40} />,
  },
  {
    id: "frontend",
    title: "Frontend Developer",
    desc: "Master HTML, CSS, JavaScript, and React questions with real-world coding challenges.",
    icon: <Globe size={40} />,
  },
  {
    id: "backend",
    title: "Backend Developer",
    desc: "Prepare for Node.js, Express, MongoDB, and database design interviews.",
    icon: <Database size={40} />,
  },
  {
    id: "fullstack",
    title: "Full Stack Developer",
    desc: "End-to-end coding interview prep for both frontend and backend concepts.",
    icon: <Layers size={40} />,
  },
  {
    id: "devops",
    title: "DevOps Engineer",
    desc: "Covers CI/CD, AWS, Docker, Kubernetes, pipelines and scaling strategies.",
    icon: <GitBranch size={40} />,
  },
  {
    id: "ai-ml",
    title: "AI / ML Engineer",
    desc: "Learn AI fundamentals, TensorFlow, and ML interview concepts.",
    icon: <Cpu size={40} />,
  },
];

const PrepHome = () => {
  const navigate = useNavigate();

  return (
    <ScreenWrapper>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Header>AI Interview Preparation</Header>
        <SubTitle>
          Select a role to start your personalized AI interview journey.
        </SubTitle>
      </motion.div>

      <CardsGrid>
        {profiles.map((p, index) => (
          <Card
            key={p.id}
            as={motion.div}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: index * 0.1 }}
            onClick={() => navigate(`/interview/${p.id}`)}
          >
            <IconWrapper>{p.icon}</IconWrapper>
            <CardTitle>{p.title}</CardTitle>
            <CardDesc>{p.desc}</CardDesc>
            <GradientButton>Start Prep →</GradientButton>
          </Card>
        ))}
      </CardsGrid>
    </ScreenWrapper>
  );
};

export default PrepHome;

// ---------------- Styled Components ----------------

const ScreenWrapper = styled.section`
  min-height: 100vh;
  width: 100%;
  background: radial-gradient(circle at top left, #0b0617, #000);
  color: #fff;
  padding: 80px 20px;
  text-align: center;
`;

const Header = styled.h1`
  font-size: 2.2rem;
  font-weight: 700;
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 12px;
`;

const SubTitle = styled.p`
  font-size: 1rem;
  color: #c8baff;
  margin-bottom: 50px;
  max-width: 550px;
  margin-left: auto;
  margin-right: auto;
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 35px;
  justify-items: center;
  max-width: 1100px;
  margin: 0 auto;
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(155, 92, 255, 0.3);
  border-radius: 20px;
  padding: 40px 25px;
  text-align: center;
  width: 100%;
  max-width: 320px;
  backdrop-filter: blur(8px);
  box-shadow: 0 6px 20px rgba(155, 92, 255, 0.15);
  transition: 0.35s ease;
  cursor: pointer;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-8px) scale(1.03);
    border-color: rgba(197, 159, 255, 0.6);
    box-shadow: 0 10px 25px rgba(155, 92, 255, 0.3);
  }
`;

const IconWrapper = styled.div`
  background: rgba(155, 92, 255, 0.15);
  border: 1px solid rgba(197, 159, 255, 0.4);
  color: #c59fff;
  border-radius: 50%;
  padding: 12px;
  width: 60px;
  height: 60px;
  margin: 0 auto 18px auto;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const CardTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #c59fff;
  margin-bottom: 12px;
`;

const CardDesc = styled.p`
  font-size: 0.95rem;
  color: #eaeaea;
  opacity: 0.9;
  line-height: 1.5;
  margin-bottom: 25px;
`;

const GradientButton = styled.button`
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 10px 20px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 0 15px rgba(155, 92, 255, 0.25);

  &:hover {
    transform: translateY(-2px) scale(1.03);
    box-shadow: 0 0 25px rgba(155, 92, 255, 0.5);
  }
`;
